import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiCalendar, 
  FiClock, 
  FiMapPin, 
  FiUser, 
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiFilter,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './HistoricoMembro.css';

const HistoricoMembro = () => {
  const { membroId } = useParams();
  const navigate = useNavigate();
  
  const [membro, setMembro] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    tipo_visita: '',
    status: '',
    presente: '',
    data_inicio: '',
    data_fim: ''
  });
  const [estatisticas, setEstatisticas] = useState(null);

  const tiposVisita = [
    { value: 'mocidade', label: 'Visita - Mocidade' },
    { value: 'criancas', label: 'Visita - Crianças' },
    { value: 'ajuntamento', label: 'Ajuntamento' },
    { value: 'reuniao_familiar', label: 'Reunião Familiar' },
    { value: 'reuniao_evangelizacao', label: 'Reunião Evangelização' }
  ];

  const statusOptions = [
    { value: 'planejada', label: 'Planejada' },
    { value: 'realizada', label: 'Realizada' },
    { value: 'cancelada', label: 'Cancelada' }
  ];

  const presencaOptions = [
    { value: 'true', label: 'Presente' },
    { value: 'false', label: 'Ausente' }
  ];

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      
      // Carregar dados do membro
      const membroResponse = await api.get(`/irmandade/${membroId}`);
      setMembro(membroResponse.data);
      
      // Carregar histórico de visitas
      const params = new URLSearchParams();
      Object.keys(filtros).forEach(key => {
        if (filtros[key]) {
          params.append(key, filtros[key]);
        }
      });
      
      const historicoResponse = await api.get(`/visitas/membro/${membroId}/historico?${params.toString()}`);
      setHistorico(historicoResponse.data);
      
      // Calcular estatísticas
      calcularEstatisticas(historicoResponse.data);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar histórico do membro');
      navigate('/visitas');
    } finally {
      setLoading(false);
    }
  }, [membroId, filtros, navigate]);

  const calcularEstatisticas = (dados) => {
    const total = dados.length;
    const presentes = dados.filter(v => v.presente).length;
    const ausentes = total - presentes;
    const percentualPresenca = total > 0 ? Math.round((presentes / total) * 100) : 0;
    
    // Estatísticas por tipo
    const porTipo = {};
    tiposVisita.forEach(tipo => {
      const visitasTipo = dados.filter(v => v.tipo_visita === tipo.value);
      const presentesTipo = visitasTipo.filter(v => v.presente).length;
      porTipo[tipo.value] = {
        total: visitasTipo.length,
        presentes: presentesTipo,
        percentual: visitasTipo.length > 0 ? Math.round((presentesTipo / visitasTipo.length) * 100) : 0
      };
    });
    
    // Estatísticas por status
    const porStatus = {};
    statusOptions.forEach(status => {
      const visitasStatus = dados.filter(v => v.status === status.value);
      porStatus[status.value] = visitasStatus.length;
    });
    
    setEstatisticas({
      total,
      presentes,
      ausentes,
      percentualPresenca,
      porTipo,
      porStatus
    });
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const limparFiltros = () => {
    setFiltros({
      tipo_visita: '',
      status: '',
      presente: '',
      data_inicio: '',
      data_fim: ''
    });
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarHora = (hora) => {
    return hora ? hora.substring(0, 5) : '';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'planejada': { class: 'status-planejada', text: 'Planejada' },
      'realizada': { class: 'status-realizada', text: 'Realizada' },
      'cancelada': { class: 'status-cancelada', text: 'Cancelada' }
    };
    
    const statusInfo = statusMap[status] || { class: 'status-default', text: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const getTipoVisitaLabel = (tipo) => {
    const tipoInfo = tiposVisita.find(t => t.value === tipo);
    return tipoInfo ? tipoInfo.label : tipo;
  };

  const getPresencaBadge = (presente) => {
    return presente ? (
      <span className="presenca-badge presente">
        <FiCheckCircle /> Presente
      </span>
    ) : (
      <span className="presenca-badge ausente">
        <FiXCircle /> Ausente
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando histórico...</p>
      </div>
    );
  }

  if (!membro) {
    return (
      <div className="error-container">
        <h2>Membro não encontrado</h2>
        <p>O membro solicitado não foi encontrado.</p>
        <Link to="/visitas" className="btn btn-primary">
          <FiArrowLeft /> Voltar para Visitas
        </Link>
      </div>
    );
  }

  return (
    <div className="historico-membro-container">
      {/* Header */}
      <div className="historico-header">
        <div className="header-navigation">
          <button 
            onClick={() => navigate('/visitas')} 
            className="btn btn-secondary"
          >
            <FiArrowLeft /> Voltar
          </button>
        </div>
        
        <div className="header-content">
          <div className="membro-info">
            <div className="membro-avatar">
              {membro.nome.charAt(0).toUpperCase()}
            </div>
            <div className="membro-dados">
              <h1>{membro.nome}</h1>
              <div className="membro-categoria">{membro.categoria}</div>
              <div className="membro-detalhes">
                {membro.data_nascimento && (
                  <span>Nascimento: {formatarData(membro.data_nascimento)}</span>
                )}
                {membro.telefone && (
                  <span>Telefone: {membro.telefone}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      {estatisticas && (
        <div className="estatisticas-section">
          <h2>Estatísticas de Participação</h2>
          
          <div className="stats-grid">
            <div className="stat-card total">
              <div className="stat-icon">
                <FiCalendar />
              </div>
              <div className="stat-content">
                <span className="stat-value">{estatisticas.total}</span>
                <span className="stat-label">Total de Visitas</span>
              </div>
            </div>
            
            <div className="stat-card presentes">
              <div className="stat-icon">
                <FiCheckCircle />
              </div>
              <div className="stat-content">
                <span className="stat-value">{estatisticas.presentes}</span>
                <span className="stat-label">Presenças</span>
              </div>
            </div>
            
            <div className="stat-card ausentes">
              <div className="stat-icon">
                <FiXCircle />
              </div>
              <div className="stat-content">
                <span className="stat-value">{estatisticas.ausentes}</span>
                <span className="stat-label">Ausências</span>
              </div>
            </div>
            
            <div className="stat-card percentual">
              <div className="stat-icon">
                {estatisticas.percentualPresenca >= 75 ? (
                  <FiTrendingUp />
                ) : estatisticas.percentualPresenca >= 50 ? (
                  <FiMinus />
                ) : (
                  <FiTrendingDown />
                )}
              </div>
              <div className="stat-content">
                <span className="stat-value">{estatisticas.percentualPresenca}%</span>
                <span className="stat-label">Taxa de Presença</span>
              </div>
            </div>
          </div>
          
          {/* Estatísticas por Tipo */}
          <div className="stats-por-tipo">
            <h3>Participação por Tipo de Visita</h3>
            <div className="tipo-stats-grid">
              {tiposVisita.map(tipo => {
                const stats = estatisticas.porTipo[tipo.value];
                if (stats.total === 0) return null;
                
                return (
                  <div key={tipo.value} className="tipo-stat-card">
                    <div className="tipo-header">
                      <span className="tipo-label">{tipo.label}</span>
                      <span className="tipo-percentual">{stats.percentual}%</span>
                    </div>
                    <div className="tipo-detalhes">
                      <span>{stats.presentes}/{stats.total} presenças</span>
                    </div>
                    <div className="tipo-progress">
                      <div 
                        className="progress-bar"
                        style={{ width: `${stats.percentual}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="filtros-section">
        <div className="filtros-header">
          <h3><FiFilter /> Filtros</h3>
          <button onClick={limparFiltros} className="btn btn-secondary btn-sm">
            Limpar Filtros
          </button>
        </div>
        
        <div className="filtros-grid">
          <div className="filter-group">
            <label>Tipo de Visita:</label>
            <select
              value={filtros.tipo_visita}
              onChange={(e) => handleFiltroChange('tipo_visita', e.target.value)}
            >
              <option value="">Todos os tipos</option>
              {tiposVisita.map(tipo => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status:</label>
            <select
              value={filtros.status}
              onChange={(e) => handleFiltroChange('status', e.target.value)}
            >
              <option value="">Todos os status</option>
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Presença:</label>
            <select
              value={filtros.presente}
              onChange={(e) => handleFiltroChange('presente', e.target.value)}
            >
              <option value="">Todas</option>
              {presencaOptions.map(opcao => (
                <option key={opcao.value} value={opcao.value}>
                  {opcao.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Data Início:</label>
            <input
              type="date"
              value={filtros.data_inicio}
              onChange={(e) => handleFiltroChange('data_inicio', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Data Fim:</label>
            <input
              type="date"
              value={filtros.data_fim}
              onChange={(e) => handleFiltroChange('data_fim', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Histórico */}
      <div className="historico-section">
        <h2>Histórico de Visitas ({historico.length})</h2>
        
        {historico.length === 0 ? (
          <div className="empty-state">
            <FiCalendar size={48} />
            <h3>Nenhuma visita encontrada</h3>
            <p>Este membro não participou de nenhuma visita com os filtros selecionados.</p>
          </div>
        ) : (
          <div className="historico-list">
            {historico.map(visita => (
              <div key={visita.id} className="historico-item">
                <div className="visita-info">
                  <div className="visita-header">
                    <div className="visita-tipo">
                      {getTipoVisitaLabel(visita.tipo_visita)}
                    </div>
                    {getStatusBadge(visita.status)}
                    {getPresencaBadge(visita.presente)}
                  </div>
                  
                  <h3 className="visita-titulo">{visita.titulo}</h3>
                  
                  <div className="visita-detalhes">
                    <div className="detalhe-item">
                      <FiCalendar />
                      <span>{formatarData(visita.data_visita)}</span>
                    </div>
                    
                    <div className="detalhe-item">
                      <FiClock />
                      <span>
                        {formatarHora(visita.hora_inicio)}
                        {visita.hora_fim && ` - ${formatarHora(visita.hora_fim)}`}
                      </span>
                    </div>
                    
                    {visita.local_visita && (
                      <div className="detalhe-item">
                        <FiMapPin />
                        <span>{visita.local_visita}</span>
                      </div>
                    )}
                    
                    {visita.responsavel_nome && (
                      <div className="detalhe-item">
                        <FiUser />
                        <span>{visita.responsavel_nome}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="visita-actions">
                  <Link 
                    to={`/visitas/${visita.id}`} 
                    className="btn btn-secondary btn-sm"
                    title="Ver detalhes da visita"
                  >
                    <FiEye /> Ver Detalhes
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoricoMembro;